/**
 * 「グループとポリシー」タブ。この人に効いているアクセスポリシー(R7d)と割当、
 * スイートのロール(直接 / 継承)。グループそのものの API は無いのでロールで代える。
 *
 * 詳細ページ本体と同じ `["idp","user",id]` を読むので、問い合わせは増えない。
 * 割当が無いときに効くのは既定のポリシーで、既定も無ければ **拒否**(fail closed)。
 */
import { Alert, Button, Form, Modal, Select, Space, Tag, Typography } from "antd";
import { useEffect, useState } from "react";

import type { IdpUserDetail } from "../../../lib/api";
import { jst } from "../../../lib/format";
import { KeyValueList, Section, StatusTag } from "../../../ui";
import { useClearUserPolicy, usePolicyGroups, useSetUserPolicy } from "../policyHooks";

export function PolicyTab({ user, canAdmin }: { user: IdpUserDetail; canAdmin: boolean }) {
  const id = user.id;
  const policy = user.policy;
  const groups = usePolicyGroups(canAdmin);
  const set = useSetUserPolicy();
  const clear = useClearUserPolicy();
  const [picked, setPicked] = useState<string | undefined>(undefined);

  // 別の人を開いたら選択を持ち越さない
  useEffect(() => {
    setPicked(policy?.groupId ?? undefined);
  }, [policy?.userId, policy?.groupId]);

  const opts = (groups.data?.items ?? []).map((g) => ({
    value: g.id,
    label: g.isDefault ? `${g.displayName}(既定)` : g.displayName,
  }));
  const busy = set.isPending || clear.isPending;

  return (
    <div className="ui-detail-grid">
      <div className="ui-detail-col">
        <Section title="アクセスポリシー">
          {policy && !policy.groupId && !policy.defaultGroupId && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="既定のポリシーが無い"
              description="割当も既定も無いので、判定を適用してもこの人には何も効かない(素通り)。既定のポリシーを決めるか、この人に割り当てる。"
            />
          )}
          <KeyValueList
            items={[
              {
                label: "効いているポリシー",
                always: true,
                value: policy?.effectiveGroupName ? (
                  <Space size={4}>
                    <span>{policy.effectiveGroupName}</span>
                    {policy.effectiveIsDefault && <Tag>既定</Tag>}
                  </Space>
                ) : (
                  <StatusTag tone="warning">なし(素通り)</StatusTag>
                ),
              },
              {
                label: "この人への割当",
                always: true,
                value: policy?.groupName || "なし(既定にしたがう)",
              },
              { label: "割当てた人", value: policy?.assignedBy },
              { label: "割当てた日時", value: jst(policy?.assignedAt) },
              { label: "既定のポリシー", always: true, value: policy?.defaultGroupName },
            ]}
          />
          {canAdmin && (
            <div style={{ marginTop: 16 }}>
              <Form.Item label="ポリシーを割り当てる" style={{ marginBottom: 8 }}>
                <Select
                  value={picked}
                  onChange={setPicked}
                  options={opts}
                  loading={groups.isLoading}
                  placeholder="ポリシーを選ぶ"
                  style={{ width: "100%" }}
                  disabled={busy}
                />
              </Form.Item>
              <Space wrap>
                <Button
                  type="primary"
                  loading={set.isPending}
                  disabled={!picked || picked === policy?.groupId}
                  onClick={() =>
                    Modal.confirm({
                      title: "このユーザーのポリシーを変える",
                      // 自分自身にも割り当てられる。次のログインから効くので、
                      // 何が何に変わるのかを出してから押させる
                      content: (
                        <>
                          <div>対象: {user.username}</div>
                          <div>いま効いている: {policy?.effectiveGroupName || "なし(素通り)"}</div>
                          <div>これから: {opts.find((o) => o.value === picked)?.label || picked}</div>
                          <div>次のログインから効く。</div>
                        </>
                      ),
                      okText: "割り当てる",
                      cancelText: "やめる",
                      onOk: () => set.mutateAsync({ id, groupId: picked! }),
                    })
                  }
                >
                  この人に割り当てる
                </Button>
                <Button
                  loading={clear.isPending}
                  disabled={!policy?.groupId}
                  onClick={() =>
                    Modal.confirm({
                      title: "このユーザーの割当を解除する",
                      content: (
                        <>
                          <div>対象: {user.username}</div>
                          <div>外す割当: {policy?.groupName || "—"}</div>
                          <div>解除後に効く: {policy?.defaultGroupName || "なし(素通り)"}</div>
                        </>
                      ),
                      okText: "解除する",
                      cancelText: "やめる",
                      onOk: () => clear.mutateAsync(id),
                    })
                  }
                >
                  割当を解除(既定にもどす)
                </Button>
              </Space>
              <div style={{ marginTop: 8 }}>
                <Typography.Text type="secondary">
                  解除すると既定のポリシーが効く。ポリシーそのものの中身は「アクセスポリシー」画面で直す。
                </Typography.Text>
              </div>
            </div>
          )}
        </Section>
      </div>
      <div className="ui-detail-col">
        <Section title="権限(スイートのロール)">
          <KeyValueList
            items={[
              {
                label: "直接の割当",
                always: true,
                value: user.access.directRoles.length ? (
                  <Space size={[4, 4]} wrap>
                    {user.access.directRoles.map((r) => (
                      <Tag key={r}>{r}</Tag>
                    ))}
                  </Space>
                ) : (
                  ""
                ),
              },
              {
                label: "グループ由来",
                value: user.access.inheritedRoles.length ? (
                  <Space size={[4, 4]} wrap>
                    {user.access.inheritedRoles.map((r) => (
                      <Tag key={r}>{r}</Tag>
                    ))}
                  </Space>
                ) : (
                  ""
                ),
              },
            ]}
            unsetLabel="なし"
          />
          <div style={{ marginTop: 8 }}>
            <Typography.Text type="secondary">
              直接の割当は「編集」で変える。グループ由来はここでは外せない。
            </Typography.Text>
          </div>
        </Section>
      </div>
    </div>
  );
}
